import type {
  AddStaffFieldStates,
  AddStaffValidatedField,
} from "./addStaffPanel.contract";
import {
  getEmploymentFieldVisibility,
  getVacationFieldVisibility,
} from "../shared/staffEmploymentFieldVisibility.logic";

export type AddStaffSectionName =
  | "personal-details"
  | "contract"
  | "terms"
  | "vacation"
  | "payment";

export type AddStaffSectionCompletion = Readonly<
  Record<AddStaffSectionName, boolean>
>;

interface VisibleSectionsParams {
  readonly employmentType: string | undefined;
  readonly salaryType: string | undefined;
}

type AddStaffSectionFields = Readonly<
  Record<AddStaffSectionName, readonly AddStaffValidatedField[]>
>;

const toSectionFields = <TFields extends AddStaffSectionFields>(
  sectionFields: TFields &
    Record<
      Exclude<AddStaffValidatedField, TFields[AddStaffSectionName][number]>,
      never
    >,
): AddStaffSectionFields => sectionFields;

const SECTION_FIELDS = toSectionFields({
  "personal-details": [
    "phoneNumber",
    "firstName",
    "lastName",
    "email",
    "personalIdentityNumber",
    "employeeNumber",
  ],
  contract: [
    "role",
    "teamIds",
    "employmentType",
    "employeeCategory",
    "startDate",
    "endDate",
    "salaryType",
    "salaryAmount",
    "workPercentage",
    "taxTable",
  ],
  terms: ["agreement", "noticeDays", "vacationDays", "workplace"],
  vacation: [
    "maxSickLeaveDays",
    "maxCareOfChildDays",
    "maxParentalLeaveDays",
    "advanceVacationDays",
  ],
  payment: ["paymentType", "paymentMethodValue"],
});

/**
 * The sections a panel shows, in the order they are rendered. The contract
 * terms section only exists for employment types that have contract terms, and
 * the vacation section only for salary models that earn vacation days.
 */
export const getVisibleAddStaffSections = ({
  employmentType,
  salaryType,
}: VisibleSectionsParams): AddStaffSectionName[] => {
  const sections: AddStaffSectionName[] = ["personal-details", "contract"];
  if (getEmploymentFieldVisibility(employmentType).showContractTerms) {
    sections.push("terms");
  }
  if (getVacationFieldVisibility(salaryType).showVacationEarningDays) {
    sections.push("vacation");
  }
  sections.push("payment");
  return sections;
};

export const getAddStaffSectionCompletion = (
  fieldStates: AddStaffFieldStates,
): AddStaffSectionCompletion => {
  const isSectionComplete = (section: AddStaffSectionName): boolean =>
    SECTION_FIELDS[section].every((field) => fieldStates[field].valid);

  return {
    "personal-details": isSectionComplete("personal-details"),
    contract: isSectionComplete("contract"),
    terms: isSectionComplete("terms"),
    vacation: isSectionComplete("vacation"),
    payment: isSectionComplete("payment"),
  };
};

export const hasCompletedAddStaffSections = ({
  sectionCompletion,
  visibleSections,
}: {
  readonly sectionCompletion: AddStaffSectionCompletion;
  readonly visibleSections: readonly AddStaffSectionName[];
}): boolean => visibleSections.every((section) => sectionCompletion[section]);
